import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onAuthChange } from './services/authService';
import { User, signInWithCustomToken } from 'firebase/auth';
import { auth } from '@obis/firebase-config';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db as firestoreDb } from '@obis/firebase-config';
import { BranchProvider } from './context/BranchContext';
import PlatformFooter from './components/common/PlatformFooter';
import { GlobalHeader } from './components/@shared/GlobalHeader';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import LeadManagement from './components/LeadManagement';
import ApplicationManagement from './components/ApplicationManagement';
import StakeholderManagement from './components/StakeholderManagement';
import CommunicationCenter from './components/CommunicationCenter';
import Analytics from './components/Analytics';
import AIAnalyticsDashboard from './components/AIAnalyticsDashboard';
import UserProfile from './components/UserProfile';
import LeadDetails from './components/LeadDetails';
import SystemActivity from './components/SystemActivity';
import Login from './components/Login';
import EventRegistrationForm from './components/EventRegistrationForm';
import TeamCompetitionBoard from './components/TeamCompetitionBoard';
import IndividualLeaderboard from './components/IndividualLeaderboard';
import WalletDashboard from './components/WalletDashboard';
import ReferralTrackingBoard from './components/ReferralTrackingBoard';
import ContactMessagesPage from './pages/ContactMessagesPage';
import ModuleLauncher from './components/ModuleLauncher';
import SchoolProfileSetup from './components/tenantAdmin/SchoolProfileSetup';
import {
  SuperAdminDashboard,
  SchoolsList,
  OnboardingPipeline,
  PlatformOverview,
  OwnerPerformanceDashboard
} from './components/superadmin';
import { ObisSpinnerFullScreen } from './components/ObisSpinner';
import { resolveUserDisplayName, formatRoleLabel, exchangePortalSsoToken } from '@obis/config';
import './App.css';

function App() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('');
  const [userName, setUserName] = useState('');
  const [profilePictureUrl, setProfilePictureUrl] = useState<string>('');

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      const params = new URLSearchParams(window.location.search);
      const ssoToken = params.get('sso_token');
      const userId = params.get('user_id');
      if (ssoToken && userId && auth) {
        try {
          const { customToken } = await exchangePortalSsoToken({
            userId,
            ssoToken,
            module: 'crm',
          });
          await signInWithCustomToken(auth, customToken);
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (e) {
          console.warn('CRM SSO exchange failed', e);
        }
      }
      if (cancelled) return;

      unsubscribe = onAuthChange(async (user) => {
        setCurrentUser(user);
        if (user) {
          const token = await user.getIdToken();

          try {
            let userData: any = null;
            const uidDoc = await getDoc(doc(firestoreDb, 'users', user.uid));
            const uidData = uidDoc.exists() ? uidDoc.data() : null;

            if (uidData && uidData.role && !uidData.isDeleted) {
              userData = uidData;
            } else if (user.email) {
              const emailQ = query(
                collection(firestoreDb, 'users'),
                where('email', '==', user.email)
              );
              const emailResults = await getDocs(emailQ);
              if (!emailResults.empty) {
                const validDoc = emailResults.docs.find(d => !d.data().isDeleted) || emailResults.docs[0];
                userData = validDoc.data();
              }
            }

            if (userData) {
              const name = resolveUserDisplayName(userData, user.email);
              const role = userData.role || 'staff';
              setUserName(name);
              setUserRole(role);
              if (userData.profile?.avatar) setProfilePictureUrl(userData.profile.avatar);
            } else {
              setUserName(user.email?.split('@')[0] || 'User');
              setUserRole('staff');
            }
          } catch (err) {
            console.error('Error loading user profile from Firestore:', err);
            setUserName(user.email?.split('@')[0] || 'User');
            setUserRole('staff');
          }

          try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';
            const response = await fetch(`${API_URL}/users/${user.uid}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success && data.data.profilePictureUrl) {
              setProfilePictureUrl(data.data.profilePictureUrl);
            }
          } catch (error) {
            // Non-critical
          }
        } else {
          localStorage.removeItem('token');
          setProfilePictureUrl('');
          setUserName('');
          setUserRole('');
        }
        setLoading(false);
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  if (loading) {
    return <ObisSpinnerFullScreen text="Loading CRM Platform..." />;
  }

  if (!currentUser) {
    return (
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/event-register" element={<EventRegistrationForm />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    );
  }

  const userInfo = {
    name: userName || currentUser.email?.split('@')[0] || 'User',
    role: userRole || 'staff',
    email: currentUser.email || '',
    profilePictureUrl: profilePictureUrl
  };

  return (
    <Router>
      <BranchProvider userRole={userRole}>
        <BranchSelectorWrapper userRole={userRole} userInfo={userInfo} />
      </BranchProvider>
    </Router>
  );
}

const BranchSelectorWrapper: React.FC<{
  userRole: string;
  userInfo: { name: string; role: string; email: string; profilePictureUrl: string }
}> = ({ userRole, userInfo }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const location = useLocation();

  // Unified tenant context (defaults to default-tenant on standalone CRM domain)
  const currentTenant = 'Olive Bloom International School';

  return (
    <>
      <GlobalHeader
        tenant={{
          name: currentTenant,
          verse: 'OBIS OPERATING SYSTEM',
          verseRef: 'COMMAND CENTER',
        }}
        user={{
          name: userInfo.name,
          role: formatRoleLabel(userInfo.role),
          avatar: userInfo.profilePictureUrl
        }}
        currentModule="crm"
        branch={selectedBranch === 'all' ? 'All Branches' : (selectedBranch.charAt(0).toUpperCase() + selectedBranch.slice(1))}
        session="2026-27"
        onMenuClick={() => setSidebarOpen(!sidebarOpen)}
        onModuleSwitch={(mod) => {
          const isLocal =
            window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
          const moduleUrls: Record<string, string> = {
            erp: isLocal ? 'http://localhost:3000' : 'https://obis-edunexus-erp.web.app',
            finance: isLocal ? 'http://localhost:3004' : 'https://obis-edunexus-finance.web.app',
            hrms: isLocal ? 'http://localhost:5173' : 'https://obis-edunexus-hrms.web.app',
            lms: '',
          };
          if (mod === 'crm') return;
          if (mod === 'lms') {
            alert('LMS System is coming soon!');
            return;
          }
          if (moduleUrls[mod]) {
            window.location.href = moduleUrls[mod];
          }
        }}
        onBranchChange={(newBranch) => {
          setSelectedBranch(newBranch === 'All Branches' ? 'all' : newBranch.toLowerCase());
        }}
        onLogout={() => {
          import('./services/authService').then(({ logout }) => logout());
        }}
      />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentUser={userInfo}
      />

      <div className="fixed top-[60px] left-0 lg:left-64 right-0 bottom-0 overflow-y-auto bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50/20 z-10 no-scrollbar">
        <div style={{ zoom: 0.95 }}>
          <div className="max-w-[1400px] mx-auto p-8" style={{ minHeight: 'calc(100vh - 60px)' }}>
            <Routes>
              {/* Flattened Primary CRM Routes */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard branchId={selectedBranch} />} />
              <Route path="/leads" element={<LeadManagement branchId={selectedBranch} />} />
              <Route path="/leads/:leadId" element={<LeadDetails />} />
              <Route path="/applications" element={<ApplicationManagement branchId={selectedBranch} />} />
              <Route path="/stakeholders" element={<StakeholderManagement />} />
              <Route path="/communications" element={<CommunicationCenter />} />
              <Route path="/contact-messages" element={<ContactMessagesPage />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/ai-analytics" element={<AIAnalyticsDashboard branchId={selectedBranch} />} />
              <Route path="/system-activity" element={<SystemActivity />} />
              <Route path="/profile" element={<UserProfile />} />
              <Route path="/event-register" element={<EventRegistrationForm />} />
              
              <Route path="/admissions-game/teams" element={<TeamCompetitionBoard />} />
              <Route path="/admissions-game/leaderboard" element={<IndividualLeaderboard />} />
              <Route path="/admissions-game/wallet" element={<WalletDashboard />} />
              <Route path="/admissions-game/referrals" element={<ReferralTrackingBoard />} />
              
              {/* Legacy / Compatibility routes */}
              <Route path="/app/:tenantSlug/crm/dashboard" element={<Navigate to="/dashboard" replace />} />
              <Route path="/app/default-tenant/crm/dashboard" element={<Navigate to="/dashboard" replace />} />
              <Route path="/app/:tenantSlug" element={<Navigate to="/dashboard" replace />} />

              {/* Launcher & Profile (Accessible via direct link if needed) */}
              <Route path="/setup" element={<ModuleLauncher />} />
              <Route path="/settings/school-profile" element={<SchoolProfileSetup />} />

              {/* Fallback */}
              <Route path="*" element={
                <div className="bg-white p-20 rounded-3xl shadow-xl text-center">
                  <h2 className="text-2xl font-black text-gray-900 mb-2">404 - Module View Not Found</h2>
                  <p className="text-gray-500">The path you requested does not exist in the CRM Engine.</p>
                  <button onClick={() => window.location.href = '/dashboard'} className="mt-6 btn-primary">Return to Dashboard</button>
                </div>
              } />
            </Routes>
          </div>

          <footer className="bg-white border-t border-gray-200 mt-8">
            <PlatformFooter />
          </footer>
        </div>
      </div>
    </>
  );
}

export default App;